/**
 * Data layer for `branded-partner-portal-per-sub-network`.
 * Raw `fetch()` confined to this file per dash contract (D6).
 * BE shape: /api/v1/branded-partner-portal-per-sub-network
 *   POST { network_id, brand_name, logo_url?, primary_color?, custom_domain?, support_email? }
 *   GET  -> { items: PortalRow[] }
 *   GET  /:network_id -> { item: PortalRow }
 */

export interface PortalRow {
	id: number;
	tenant_id: string;
	network_id: string;
	brand_name: string;
	logo_url: string | null;
	primary_color: string | null;
	custom_domain: string | null;
	support_email: string | null;
	created_at: number;
	updated_at: number;
}

export interface ListPortalResponse {
	items: PortalRow[];
}

export interface DetailPortalResponse {
	item: PortalRow;
}

export interface CreatePortalBody {
	network_id: string;
	brand_name: string;
	logo_url?: string | null;
	primary_color?: string | null;
	custom_domain?: string | null;
	support_email?: string | null;
}

export interface CreatePortalResponse {
	ok: boolean;
	network_id: string;
	created_at: number;
}

const BASE = "/api/v1/branded-partner-portal-per-sub-network";

// ---- pure validators (exported for unit tests) ----
export const SUBDOMAIN_RE = /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/;
export const HEX_COLOR_RE = /^#[0-9a-fA-F]{6}$/;
export const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Validate a sub-network subdomain slug.
 * Rules: lowercase a-z, digits, hyphens; cannot start/end with hyphen; 1-63 chars.
 */
export function validateSubdomain(input: string): { ok: true } | { ok: false; error: string } {
	const v = (input ?? "").trim();
	if (!v) return { ok: false, error: "subdomain required" };
	if (v.length > 63) return { ok: false, error: "subdomain too long (max 63)" };
	if (!SUBDOMAIN_RE.test(v)) return { ok: false, error: "subdomain must be lowercase letters, digits, hyphens" };
	return { ok: true };
}

/**
 * Build the CNAME hint a partner must configure to point their custom
 * domain at their branded sub-network portal. Pure helper — no network I/O.
 */
export function cnameHint(subdomain: string, customDomain: string): string {
	const sub = (subdomain ?? "").trim().toLowerCase();
	const dom = (customDomain ?? "").trim().toLowerCase();
	if (!sub || !dom) return "";
	return `${dom}. CNAME ${sub}.portal.trackerity.app.`;
}

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListPortalResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListPortalResponse>(res);
}

export async function detail(network_id: string): Promise<DetailPortalResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(network_id)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<DetailPortalResponse>(res);
}

export async function create(body: CreatePortalBody): Promise<CreatePortalResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreatePortalResponse>(res);
}

export const brandedPartnerPortalPerSubNetworkQueryKeys = {
	all: ["branded-partner-portal-per-sub-network"] as const,
	list: () => [...brandedPartnerPortalPerSubNetworkQueryKeys.all] as const,
	detail: (id: string) => [...brandedPartnerPortalPerSubNetworkQueryKeys.all, id] as const,
};
